import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { Product, productTypeTranslations, Supplement } from '@digilize/shared/definitions/src';

@Component({
  selector: 'lib-grouped-items-list',
  templateUrl: './grouped-items-list.component.html',
  styleUrls: ['./grouped-items-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class GroupedItemsListComponent {
  @Output() selectionChange = new EventEmitter();
  @Input() groupedProducts;
  @Output() productSelected = new EventEmitter<Product | Supplement>();
  productTypeTranslations = productTypeTranslations;

  trackByGroupId(_: number, group: { id: string | null }): string | null {
    return group.id;
  }

  trackByProductId(_: number, product: Product | Supplement): string {
    return (product as Product).id;
  }

  // Request a square, center-cropped thumbnail from the media resizer instead of loading the
  // full-size image (m=cover scales to cover the box and center-crops to exactly w×h).
  getThumbnailUrl(url: string): string {
    if (!url) {
      return url;
    }
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}w=300&h=300&m=cover`;
  }

  // `products` holds only what the caller's search left in view, so a group narrowed to one product
  // would otherwise claim the whole group is selected (PAUL-3333). `total` is the group's real size;
  // callers that never filter omit it and keep counting what they render.
  private groupSize(group): number {
    return group?.total ?? group?.products?.length ?? 0;
  }

  private selectedCount(group): number {
    return group?.products?.filter((product) => product.isSelected).length || 0;
  }

  isGroupSelected(group) {
    const size = this.groupSize(group);
    return size > 0 && this.selectedCount(group) >= size;
  }

  isGroupPartiallySelected(group) {
    const selectedCount = this.selectedCount(group);
    return selectedCount > 0 && selectedCount < this.groupSize(group);
  }

  toggleSelectGroup(event, group) {
    group?.products.forEach((product) => {
      this.toggleSelectProduct(event, product);
    });
  }

  toggleSelectProduct(event, product: Product | Supplement) {
    product.isSelected = event?.checked;
    this.productSelected.emit(product);
    this.selectionChange.emit();
  }
}
